'use client';

import React, {useEffect} from 'react';
import {observer} from 'mobx-react-lite';
import {createPortal} from 'react-dom';
import {X} from 'lucide-react';

import {cn} from '@/libs/utils';
import {useModalStore} from '@/presentation/hooks/useStores';
import type {ModalConfig, ModalSize} from '@/presentation/stores/ModalStore';

const resetBodyOverflow = () => {
    document.body.style.removeProperty('overflow');
    if (!document.body.getAttribute('style')?.trim()) {
        document.body.removeAttribute('style');
    }
};

interface ModalRendererProps {
    modal: ModalConfig;
    onClose: (id: string) => void;
    onBackdropClick: (id: string) => void;
}

const ModalRenderer = ({modal, onClose, onBackdropClick}: ModalRendererProps) => {
    const {component: Component, props = {}, options = {}} = modal;

    const sizeClasses: Record<ModalSize, string> = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg',
        xl: 'max-w-xl',
        full: 'max-w-full w-full h-full',
    };

    const size = options.size || 'md';

    return (
        <>
            {/* Modal Container with backdrop inside */}
            <div
                className={cn(
                    'fixed inset-0 z-[9999] flex items-center justify-center p-4',
                    options.overlayClassName
                )}
                onClick={(e) => {
                    if (e.target === e.currentTarget && options.backdrop !== false) {
                        onBackdropClick(modal.id);
                    }
                }}
            >
                {/* Backdrop - inside container, behind modal content */}
                {/* Note: Using dark blue (#000032) instead of pure black due to rendering issues with rgba(0,0,0,x) */}
                {options.backdrop !== false && (
                    <div
                        className="absolute inset-0 bg-[#000032] opacity-60 pointer-events-none"
                        aria-hidden="true"
                    />
                )}

                {/* Modal Content - relative to sit above backdrop */}
                <div
                    className={cn(
                        'relative bg-white rounded-lg shadow-lg max-h-[90vh] overflow-auto',
                        'animate-in fade-in-0 zoom-in-95 duration-200',
                        sizeClasses[size],
                        size === 'full' && 'rounded-none',
                        options.className
                    )}
                    onClick={(e) => e.stopPropagation()}
                >
                {/* Close button */}
                {options.dismissible !== false && (
                    <button
                        onClick={() => onClose(modal.id)}
                        className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none z-10"
                    >
                        <X className="h-4 w-4"/>
                        <span className="sr-only">Close</span>
                    </button>
                )}

                {/* Modal Component */}
                    <Component
                        {...props}
                        onClose={() => onClose(modal.id)}
                        modalId={modal.id}
                    />
                </div>
            </div>
        </>
    );
};

const GlobalModalRenderer = observer(() => {
    const modalStore = useModalStore();

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                modalStore.handleEscapeKey();
            }
        };

        if (modalStore.hasActiveModals) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        } else {
            resetBodyOverflow();
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [modalStore, modalStore.hasActiveModals]);

    // Clean up body overflow on unmount
    useEffect(() => {
        return () => {
            resetBodyOverflow();
        };
    }, []);

    if (!modalStore.hasActiveModals) {
        return null;
    }

    // Only render if we're on the client side
    if (typeof window === 'undefined') {
        return null;
    }

    return createPortal(
        <>
            {modalStore.allModals.map((modal) => (
                <ModalRenderer
                    key={modal.id}
                    modal={modal}
                    onClose={(id) => modalStore.closeModal(id)}
                    onBackdropClick={(id) => modalStore.handleBackdropClick(id)}
                />
            ))}
        </>,
        document.body
    );
});

interface ModalProviderProps {
    children: React.ReactNode;
}

export const ModalProvider: React.FC<ModalProviderProps> = ({children}) => {
    return (
        <>
            {children}
            <GlobalModalRenderer/>
        </>
    );
};

ModalProvider.displayName = 'ModalProvider';

export default ModalProvider;
